import { useState, useEffect } from 'react';
import {
	SimpleGrid,
	Flex,
	Stack,
	Heading,
	Icon,
	Image,
	Tooltip,
	Spacer,
	Spinner,
	StackDivider,
	Badge,
} from '@chakra-ui/react';
import { BiDollar } from 'react-icons/bi';
import { AiFillHeart, AiOutlineHeart, AiOutlineLineChart } from 'react-icons/ai';
import { VscEdit } from 'react-icons/vsc';
import { IoIosArrowRoundUp, IoIosArrowRoundDown } from "react-icons/io";
import { MdGroupWork, MdImage } from 'react-icons/md';
import { RiRefreshLine } from 'react-icons/ri';
import { HiSparkles } from 'react-icons/hi';
import { FaEraser } from 'react-icons/fa';
import { ItemModel, ArtistModel } from 'models';
import Feature from '../../Feature/Feature';
import GrowthBadge from '../../Badge/GrowthBadge';
import BasicArtistCard from 'components/Artist/BasicArtistCard/BasicArtistCard';

export interface ItemContainerProps {
	item: ItemModel;
	artist: ArtistModel | null | undefined;
	isFollowingArtist: boolean;
	followArtistLoading: boolean;
	onEditClick: () => void;
	onRankChangeClick?: () => void;
	onGroupingClick?: () => void;
	isDemoted?: boolean;
	onFollowArtistClick: () => void;
	onArtistClick: () => void;
	onRefreshMarketClick?: () => void;
	refreshMarketLoading?: boolean;
	onEnhanceClick?: () => void;
	enhanceLoading?: boolean;
	onRemoveBackgroundClick?: () => void;
	removeBackgroundLoading?: boolean;
	onMigrateImageClick?: () => void;
	migrateImageLoading?: boolean;
}

export enum ItemContainerSizingConfig {
	IMAGE_MAX_HEIGHT = '500px',
	SECTION_SPACING = '20px',
}

export default function ItemContainer({
	item,
	artist,
	isFollowingArtist,
	followArtistLoading,
	onEditClick,
	onRankChangeClick,
	onGroupingClick,
	isDemoted = false,
	onFollowArtistClick,
	onArtistClick,
	onRefreshMarketClick,
	refreshMarketLoading = false,
	onEnhanceClick,
	enhanceLoading = false,
	onRemoveBackgroundClick,
	removeBackgroundLoading = false,
	onMigrateImageClick,
	migrateImageLoading = false,
}: ItemContainerProps) {
	const [artistData, setArtistData] = useState<ArtistModel | null | undefined>(artist);

	useEffect(() => {
		if (artist) {
			setArtistData(artist);
		}
	}, [artist]);
		
	const getClosePriceColor = (): string => {
		let iconColor = 'gray';
		if (item.hasMarketData()) {
			if (item.getEstimatedMarketPrice() > item.getClosePrice()) {
				iconColor = 'red';
			} else if (item.getEstimatedMarketPrice() < item.getClosePrice()) {
				iconColor = 'green';
			}
		}
		return iconColor;
	}

	function renderContainerUserButton(icon: React.ElementType, onClick: () => void, isLoading: boolean) {
		return (
			<Flex
				w={8}
				h={8}
				align={'center'}
				justify={'center'}
				rounded={'full'}
				cursor={'pointer'}
				border="1px"
				onClick={() => {
					if (!isLoading) {
						onClick();
					}
				}}
				_hover={{ color: 'blue.500' }}
			>
				{isLoading ? <Spinner size="sm" /> : <Icon as={icon} />}
			</Flex>
		);
	}

	function renderContainerUserButtons() {
		return (
			<Flex direction="row" justifyContent="flex-start">
				<Flex width="100%" gap={2} justifyContent={'flex-start'} flexWrap="wrap">
					<Tooltip label="Edit Item">
						{renderContainerUserButton(VscEdit, onEditClick, false)}
					</Tooltip>
					{onGroupingClick && !isDemoted && (
						<Tooltip label="Create Grouping">
							{renderContainerUserButton(MdGroupWork, onGroupingClick, false)}
						</Tooltip>
					)}
					{onRefreshMarketClick && (
						<Tooltip label="Refresh Market Data">
							{renderContainerUserButton(RiRefreshLine, onRefreshMarketClick, refreshMarketLoading)}
						</Tooltip>
					)}
					{onEnhanceClick && (
						<Tooltip label="Enhance Item">
							{renderContainerUserButton(HiSparkles, onEnhanceClick, enhanceLoading)}
						</Tooltip>
					)}
					{onRemoveBackgroundClick && (
						<Tooltip label="Remove Background">
							{renderContainerUserButton(FaEraser, onRemoveBackgroundClick, removeBackgroundLoading)}
						</Tooltip>
					)}
					{onMigrateImageClick && (
						<Tooltip label="Migrate Image">
							{renderContainerUserButton(MdImage, onMigrateImageClick, migrateImageLoading)}
						</Tooltip>
					)}
					{onRankChangeClick && (
						<>
							<Tooltip label={isDemoted ? "Promote this item" : "Demote this item"}>
								{renderContainerUserButton(isDemoted ? IoIosArrowRoundUp : IoIosArrowRoundDown, onRankChangeClick, false)}
							</Tooltip>
						</>
					)}
				</Flex>
			</Flex>
		);
	}

	function renderArtistSection() {
		if (artistData) {
			const buttonText = isFollowingArtist ? 'Unfollow' : 'Follow';
			const buttonIcon = isFollowingArtist ? <AiFillHeart /> : <AiOutlineHeart />;

			return (
				<BasicArtistCard
					artist={artistData}
					avatarSize="md"
					titleSize="lg"
					showAdditionalInfo
					showButton
					onArtistTextClick={onArtistClick}
					buttonText={buttonText}
					buttonIcon={buttonIcon}
					buttonLoading={followArtistLoading}
					onButtonClick={onFollowArtistClick}
					showBorder={false}
				/>
			);
		}
	}

	function renderContainerFeatures() {
		const lastSaleIconColor = getClosePriceColor();
		const containerDivider = <StackDivider borderColor={'gray.100'} />;
		let marketPriceText: string = 'No Market Data';
		let secondaryMarketPremiumText: string = 'No Market Data';
		let lastSaleTextContent: string | JSX.Element = 'No Recorded Sales';
		if (item.hasMarketData() && item.hasReleasePriceData()) {
			secondaryMarketPremiumText = item.getSecondaryMarketPremiumForDisplay();
		} else if (item.hasMarketData() && !item.hasReleasePriceData()) {
			secondaryMarketPremiumText = 'Retail Price Unknown';
		}

		if (item.hasMarketData()) {
			marketPriceText = item.getEstimatedMarketPriceForDisplay();
			lastSaleTextContent = (
				<Flex alignItems={'center'} gap={2}>
					{item.getClosePriceForDisplay()}
					{item.hasMultipleSales() && <GrowthBadge percentage={item.getLastSalePercentageChange()} />}
				</Flex>
			);
		}

		return (
			<Stack paddingTop={ItemContainerSizingConfig.SECTION_SPACING} spacing={4} divider={containerDivider}>
				<Feature
					icon={<Icon as={BiDollar} color={'purple.500'} w={5} h={5} />}
					iconBg={'purple.100'}
					text={marketPriceText}
					subText={item.hasMarketData() ? 'Est. market price' : ''}
				/>
				<Feature
					icon={<Icon as={BiDollar} color={`${lastSaleIconColor}.500`} w={5} h={5} />}
					iconBg={`${lastSaleIconColor}.100`}
					text={lastSaleTextContent}
					subText={item.hasMarketData() ? 'Last Sale' : ''}
				/>
				<Feature
					icon={
						<Icon as={AiOutlineLineChart} color={'yellow.500'} w={5} h={5} />
					}
					iconBg={'yellow.100'}
					text={secondaryMarketPremiumText}
					subText={item.hasMarketData() ? 'Secondary Market Premium' : ''}
				/>
			</Stack>
		);
	}

	function renderItemImage() {
		return (
			<Flex justifyContent="center" alignItems="center">
				<Image
					rounded="md"
					alt={item.name}
					src={item.image}
					objectFit="contain"
					maxHeight={ItemContainerSizingConfig.IMAGE_MAX_HEIGHT}
				/>
			</Flex>
		);
	}

	return (
		<SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
			{renderItemImage()}
			<Stack spacing={4}>
				<Heading>{item.name}</Heading>
				{isDemoted && (
					<Flex justifyContent="center">
						<Badge colorScheme="red" fontSize="md" px={3} py={1}>
							DEMOTED
						</Badge>
					</Flex>
				)}
				{renderContainerUserButtons()}
				{renderArtistSection()}
				{renderContainerFeatures()}
			</Stack>
		</SimpleGrid>
	);
}
